import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { addColumn } from '../utils/schemas'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'
import { tempTableName } from '../utils'

class GeographytoGeometry extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.geographytogeometry',
        group: 'Data Preparation',
        title: 'Geography to Geometry',
        description: 'This component converts a column from geography to geometry data type.'
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'geo',
          type: WorkflowComponentParamType.Column,
          title: 'geography column',
          parent: 'source',
          dataType: [ColumnDataType.Geography]
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const preparedParams = this._prepareParameters(params)
    let query = ''
    const storedProc = codeType === WorkflowCodeType.StoredProcedure
    const geocol = preparedParams.geo
    switch (connection.type) {
      case Provider.Postgres: {
        const select = `SELECT %s
        FROM ${connection.quoteName(preparedParams.source as string)}`
        const createTable = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        let datasetname, schemaname, tablename, storedProcTable
        if (storedProc) {
          ;[datasetname, schemaname] = connection.tempLocation.split('.')
          tablename = `${tempTableName(componentName, preparedParams.source)}`
          storedProcTable = connection.quoteName(`${connection.tempLocation}.${tablename}`)
        } else {
          ;[datasetname, schemaname, tablename] = (preparedParams.source as string).split('.')
        }

        query = dedent`DECLARE
                columns_def TEXT;
              BEGIN
                ${
                  storedProc
                    ? dedent`
                        CREATE TABLE ${storedProcTable} AS
                        SELECT * FROM ${connection.quoteName(preparedParams.source as string)}
                        WHERE 1=0;`
                    : ''
                }
                EXECUTE '
                  WITH __alias AS(
                    SELECT column_name AS col_alias
                    FROM ${datasetname}.INFORMATION_SCHEMA.COLUMNS
                    WHERE table_name = ''${tablename}'' and
                      table_schema = ''${schemaname}'' and
                      table_catalog = ''${datasetname}'' and
                      column_name != ''${geocol}''
                  )
                  SELECT STRING_AGG(col_alias, '', '') aliases
                  FROM __alias
                ' INTO columns_def;
                ${storedProc ? `DROP TABLE ${storedProcTable};` : ''}
                IF columns_def IS NULL THEN
                  columns_def:= 'CAST(${geocol} AS geometry) AS ${geocol}';
                ELSE
                  columns_def:= columns_def || ', ' || 'CAST(${geocol} AS geometry) AS ${geocol}';
                END IF;
                EXECUTE
                  REPLACE(
                    '${createTable}',
                    '%s',
                    columns_def
                    );
              END;`
        break
      }
      case Provider.Redshift: {
        const tempselect = connection.getTempSelectQuery(
          dedent`SELECT %s
                  FROM ${connection.quoteName(preparedParams.source as string)}`,
          dryRun
        )
        const temptable = `${outputTablename}_temp`
        let datasetname, schemaname, tablename, storedProcTable
        if (storedProc) {
          ;[datasetname, schemaname] = connection.tempLocation.split('.')
          tablename = `${tempTableName(componentName, preparedParams.source)}`
          storedProcTable = connection.quoteName(`${schemaname}.${tablename}`)
        } else {
          ;[datasetname, schemaname, tablename] = (preparedParams.source as string).split('.')
        }
        query = dedent`
              DECLARE
                columns_def TEXT;
              BEGIN
                ${
                  storedProc
                    ? dedent`
                        CREATE TABLE ${storedProcTable} AS
                        SELECT * FROM ${connection.quoteName(preparedParams.source as string)}
                        WHERE 1=0;`
                    : ''
                }
                ${connection.getDropTableQuery(temptable)}
                EXECUTE '
                  CREATE TABLE ${connection.quoteName(temptable)} AS
                  SELECT
                    DISTINCT column_name AS col_alias
                  FROM svv_columns
                  WHERE table_name ILIKE ''${tablename}'' AND
                    table_schema ILIKE ''${schemaname}'' AND
                    table_catalog ILIKE ''${datasetname}'' AND
                    column_name != ''${geocol}''
                ';
                EXECUTE '
                    SELECT LISTAGG(col_alias, '', '') aliases
                    FROM ${connection.quoteName(temptable)};
                ' INTO columns_def;
                ${storedProc ? `DROP TABLE ${storedProcTable};` : ''}
                IF columns_def IS NULL THEN
                  columns_def:= 'CAST(${geocol} AS geometry}) AS ${geocol}';
                ELSE
                  columns_def:= columns_def || ', ' || 'CAST(${geocol} AS geometry) AS ${geocol}';
                END IF;
                ${connection.getDropTableQuery(outputTablename)}
                EXECUTE
                  REPLACE(
                    'CREATE${storedProc ? ' TEMPORARY' : ''} TABLE ${outputTablename} AS
                    ${tempselect}',
                    '%s',
                    columns_def
                );
                DROP TABLE ${temptable};
              END;`
        break
      }
      default:
        return null
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params }: GetOutputSchemaParams): TableSchema {
    return addColumn(columns.source, {
      name: params.geo as string,
      type: ColumnDataType.Geometry
    })
  }
  supportedProviders(): Provider[] {
    return [Provider.Postgres, Provider.Redshift]
  }
}

export { GeographytoGeometry }
